import { identity } from 'ramda'
import React, {
  Dispatch,
  ReactElement,
  ReactNode,
  Reducer,
  createContext,
  isValidElement,
  useContext,
} from 'react'
import { createPortal } from 'react-dom'
import { isFragment } from 'react-is'
import { useReducerWithMiddleware, useStateObject } from './hooks'

export const safeRenderPropComponent = <T,>(value: T | (() => T)): (() => T) =>
  typeof value === 'function' ? (value as () => T) : () => value

export const NoopComponent = (): null => null

export const childrenToArray = (children: ReactNode): ReactNode[] => {
  const childrenArr: ReactNode[] = []

  React.Children.forEach(children, (child) => {
    if (child === undefined || child === null) return

    if (Array.isArray(child)) {
      childrenArr.push(...childrenToArray(child))
    } else if (
      isFragment(child) &&
      React.isValidElement(child) &&
      child.props
    ) {
      childrenArr.push(
        ...childrenToArray((child as ReactElement<any>).props.children),
      )
    } else {
      childrenArr.push(child)
    }
  })

  return childrenArr
}

export type ParsedChild = {
  key?: string
  node: ReactElement
  [key: string]: any
}

export const parseChildrenArray = (children: ReactNode): ParsedChild[] =>
  childrenToArray(children)
    .filter(
      (node): node is ReactElement =>
        isValidElement(node) &&
        (node as ReactElement<any>).props?.hidden !== true,
    )
    .map((node) => {
      const key = node.key != null ? String(node.key) : undefined
      return {
        key,
        ...(node as ReactElement<any>).props,
        node,
      }
    })

export const fromEventTarget = (
  func?: (value: any) => void,
  key: string = 'value',
) => (event: { target?: { [key: string]: any } }) =>
  func && func(event?.target?.[key])

export const mapWithKey = <T,>(
  Comp: React.ComponentType<any>,
  key: keyof T,
  list: T[],
): ReactElement[] =>
  list.map((item) => <Comp key={String(item[key])} {...item} />)

type Middleware<S, A> = (state: S, action: A, next: Dispatch<A>) => void

export function makeStore<S, A>(
  initialState: S,
  reducer: Reducer<S, A>,
  middlewareFn: Middleware<S, A> = identity as Middleware<S, A>,
): [React.FC<{ children: ReactNode }>, () => Dispatch<A>, () => S] {
  const dispatchContext = createContext<Dispatch<A>>(() => undefined as never)
  const storeContext = createContext<S>(initialState)

  const StoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [store, dispatch] = useReducerWithMiddleware(
      reducer,
      initialState,
      // @ts-expect-error this is not implemented correctly. to be fixed in the future
      middlewareFn,
    )

    return (
      <dispatchContext.Provider value={dispatch}>
        <storeContext.Provider value={store}>{children}</storeContext.Provider>
      </dispatchContext.Provider>
    )
  }

  const useDispatch = () => useContext(dispatchContext)
  const useStore = () => useContext(storeContext)

  return [StoreProvider, useDispatch, useStore]
}

export const makeContext = <T extends object>() => {
  const context = createContext<{
    values: T
    dispatchValues: React.Dispatch<Partial<T>>
  } | null>(null)

  const Provider: React.FC<{ initialValue: T; children: ReactNode }> = ({
    initialValue,
    children,
  }) => {
    const [values, dispatchValues] = useStateObject<T>(initialValue)

    return (
      <context.Provider value={{ values, dispatchValues }}>
        {children}
      </context.Provider>
    )
  }

  const useCustomContext = () => {
    const ctx = useContext(context)
    if (!ctx) throw new Error('useContext must be used within its Provider')
    return ctx
  }

  return [Provider, useCustomContext] as const
}

export const maybeRenderInPortal = (
  component: ReactElement,
  parent: Element,
  shouldRenderInPortal: boolean,
): ReactElement =>
  shouldRenderInPortal ? createPortal(component, parent) : component

export const nextTick = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 0))
